import { useGameParamsContext } from "./gameParams";
import React from "react";
import { z } from "zod";
import { authenticate } from "../clients/session";
import { getAvatarId } from "../utils/jwt";
import { setUser as setUserInSentry } from "../tracing/sentry";
import { useSnowplowContext } from "../analytics/snowplow";

const LOCALSTORAGE_KEY = "session-cache";
const REFRESH_TOKEN_LIMIT = 1000 * 60 * 60 * 24; // Refresh session if it expires in less than a day
const JWT_MAX_RETRIES = 3;

const SessionState = z.object({
    sessionToken: z.string().nullable(),
    expires: z.coerce.date(),
    avatarId: z.number().nullable(),
});

type SessionState = z.infer<typeof SessionState>;

const defaultState = {
    sessionToken: null,
    expires: new Date(0),
    avatarId: null,
};

const SessionContext = React.createContext<{
    state: SessionState;
    actions: {
        refreshSession: () => Promise<void>;
    };
}>({
    state: defaultState,
    actions: {
        refreshSession: () => Promise.resolve(),
    },
});

type SessionAction =
    | { type: "UPDATE_SESSION"; session: SessionState }
    | { type: "CLEAR_SESSION" };

const reducer = (state: SessionState, action: SessionAction): SessionState => {
    switch (action.type) {
        case "UPDATE_SESSION": {
            console.log("UPDATE_SESSION");
            return {
                ...state,
                sessionToken: action.session.sessionToken,
                expires: action.session.expires,
                avatarId: action.session.avatarId,
            };
        }
        case "CLEAR_SESSION": {
            console.log("CLEAR_SESSION");
            clearLocalStorage();
            return defaultState;
        }
        default: {
            throw new Error(`Unhandled action type: ${action}`);
        }
    }
};

const SessionContextProvider = ({
    children,
    initialState,
}: {
    children: JSX.Element | JSX.Element[];
    initialState: SessionState;
}): JSX.Element | null => {
    const { gameParams } = useGameParamsContext();

    const { setUser: setUserInSnowplow } = useSnowplowContext();
    const [state, dispatch] = React.useReducer(reducer, initialState);
    const intervalId = React.useRef<number | null>(null);
    const retries = React.useRef<number>(0);

    const clearInterval = React.useCallback(() => {
        if (intervalId.current) {
            window.clearInterval(intervalId.current);
            intervalId.current = null;
        }
    }, [intervalId.current]);

    React.useEffect(() => {
        const { jwt, offline } = gameParams;
        /* FIXME:
          Remove this when native side changes are merged in all games.
          We only need to do this because the old webview plasters over the
          asynchronous nature of getting the JWT from the game server. So
          sometimes the jwt isn't in the initial `getParams` call
        */

        if (!jwt && offline === undefined) {
            console.log("NO_JWT");
            if (!intervalId.current) {
                const id = window.setInterval(() => {
                    if (
                        retries.current >= JWT_MAX_RETRIES &&
                        intervalId.current
                    ) {
                        console.log("MAX_RETRIES");
                        clearInterval();
                        // Send token expired if all our retries have completed.
                        window.sendCommand?.("tokenExpired");
                        return;
                    }

                    retries.current++;
                    console.log("GET_PARAMS");
                    window.sendCommand?.("getParams");
                }, 250);
                intervalId.current = id;
            }
        } else {
            console.log("CLEAR_INTERVAL");
            clearInterval();
        }

        return () => {
            clearInterval();
        };
    }, [gameParams]);

    React.useEffect(() => {
        const now = new Date();
        if (Number(state.expires) - Number(now) < REFRESH_TOKEN_LIMIT) {
            refreshSession();
        }
    }, [state.expires]);

    React.useEffect(() => {
        const { jwt } = gameParams;
        if (!jwt) {
            return;
        }
        const avatarId = getAvatarId(jwt) ?? null;

        if (!avatarId) {
            dispatch({ type: "CLEAR_SESSION" });
            return;
        }

        if (avatarId !== state.avatarId) {
            refreshSession();
        }
    }, [state.avatarId, gameParams]);

    const refreshSession = async () => {
        console.log("refreshSession");
        const { jwt } = gameParams;
        if (jwt) {
            await authenticate(jwt).then((session) => {
                if (!session) {
                    dispatch({ type: "CLEAR_SESSION" });
                    return;
                }

                dispatch({
                    type: "UPDATE_SESSION",
                    session,
                });
                writeToLocalStorage(session);
                const avatarId = getAvatarId(jwt);
                if (avatarId && window.SENTRY_DSN) {
                    setUserInSentry(avatarId);
                }

                if (avatarId) {
                    setUserInSnowplow(avatarId);
                }
            });
        }
    };

    return (
        <SessionContext.Provider value={{ state, actions: { refreshSession } }}>
            {children}
        </SessionContext.Provider>
    );
};

export const withSessionContextProvider =
    (Component: typeof React.Component) => (props: Record<string, unknown>) => {
        return (
            <SessionContextProvider initialState={readFromLocalStorage()}>
                <Component {...props} />
            </SessionContextProvider>
        );
    };

export const useSessionContext = () => React.useContext(SessionContext);

const readFromLocalStorage = (): SessionState => {
    try {
        const data = JSON.parse(localStorage.getItem(LOCALSTORAGE_KEY) ?? "");
        const session = SessionState.parse(data);
        return session;
    } catch {
        return defaultState;
    }
};

const writeToLocalStorage = (session: SessionState) => {
    localStorage.setItem(LOCALSTORAGE_KEY, JSON.stringify(session));
};

const clearLocalStorage = () => {
    localStorage.removeItem(LOCALSTORAGE_KEY);
};
